import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { queryKeys } from '@/lib/query-client';
import type {
  AuditPrep,
  AuditPrepListItem,
  CreateAuditPrepPayload,
  UpdateAuditPrepPayload,
  UpdateClauseResponsePayload,
  AuditClauseResponse,
  FM333Data,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

interface AuditPrepFilters {
  audit_type?: string;
  status?: string;
}

function cleanFilters(filters: Record<string, string | undefined>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useAuditPreps(filters: AuditPrepFilters = {}) {
  const cleaned = cleanFilters(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: queryKeys.auditPrep.list(cleaned),
    queryFn: async (): Promise<AuditPrepListItem[]> => {
      const response = await apiClient.get<{ results: AuditPrepListItem[] } | AuditPrepListItem[]>(
        '/api/v1/compliance/audit-prep/',
        { params: cleaned }
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: AuditPrepListItem[] }).results)) return (data as { results: AuditPrepListItem[] }).results;
      return [];
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useAuditPrep(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.auditPrep.detail(id),
    queryFn: async (): Promise<AuditPrep> => {
      const response = await apiClient.get<AuditPrep>(
        `/api/v1/compliance/audit-prep/${id}/`
      );
      return response.data;
    },
    enabled,
  });
}

export function useFM333Data(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.auditPrep.fm333Data(id),
    queryFn: async (): Promise<FM333Data> => {
      const response = await apiClient.get<FM333Data>(
        `/api/v1/compliance/audit-prep/${id}/fm333_data/`
      );
      return response.data;
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateAuditPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAuditPrepPayload): Promise<AuditPrep> => {
      const response = await apiClient.post<AuditPrep>(
        '/api/v1/compliance/audit-prep/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auditPrep.all });
    },
  });
}

export function useUpdateAuditPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateAuditPrepPayload;
    }): Promise<AuditPrep> => {
      const response = await apiClient.patch<AuditPrep>(
        `/api/v1/compliance/audit-prep/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auditPrep.all });
    },
  });
}

export function useGenerateResponses() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<{ message: string; count: number }> => {
      const response = await apiClient.post<{ message: string; count: number }>(
        `/api/v1/compliance/audit-prep/${id}/generate_responses/`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auditPrep.all });
    },
  });
}

export function useGenerateFM333() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<AuditPrep> => {
      const response = await apiClient.post<AuditPrep>(
        `/api/v1/compliance/audit-prep/${id}/generate_fm333/`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auditPrep.all });
    },
  });
}

export function useGenerateFM312() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<AuditPrep> => {
      const response = await apiClient.post<AuditPrep>(
        `/api/v1/compliance/audit-prep/${id}/generate_fm312/`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auditPrep.all });
    },
  });
}

export function useUpdateClauseResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      auditPrepId,
      responseId,
      data,
    }: {
      auditPrepId: number;
      responseId: number;
      data: UpdateClauseResponsePayload;
    }): Promise<AuditClauseResponse> => {
      const response = await apiClient.patch<AuditClauseResponse>(
        `/api/v1/compliance/audit-prep/${auditPrepId}/clause-responses/${responseId}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auditPrep.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Download helpers
// ---------------------------------------------------------------------------

export async function downloadFM333(id: number) {
  const response = await apiClient.get(
    `/api/v1/compliance/audit-prep/${id}/download_fm333/`,
    { responseType: 'blob' },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `FM-333-${id}.pdf`,
    ),
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadFM312(id: number) {
  const response = await apiClient.get(
    `/api/v1/compliance/audit-prep/${id}/download_fm312/`,
    { responseType: 'blob' },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `FM-312-${id}.pdf`,
    ),
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
